import React, { useEffect, useState } from 'react';
import { Truck, TrendingUp, DollarSign, CircleCheckBig, Clock4, MapPin } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api from '../lib/api';

const money = (n, cur = 'SAR') =>
  `${cur} ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CATEGORY_COLORS = {
  Equipment: 'bg-blue-100 text-blue-800 border-blue-200',
  Transportation: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  Material: 'bg-amber-100 text-amber-800 border-amber-200',
  Manpower: 'bg-purple-100 text-purple-800 border-purple-200',
  Other: 'bg-slate-100 text-slate-800 border-slate-200',
};

export const SalesDashboardPage = () => {
  const [active, setActive] = useState({ count: 0, dn_count: 0, items: [] });
  const [performance, setPerformance] = useState({ total_value: 0, po_count: 0, breakdown: [] });
  const [payments, setPayments] = useState({ total_received: 0, total_pending: 0, by_client: [], by_po: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [a, p, m] = await Promise.all([
          api.get('/rbac/sales-dashboard/active-equipment'),
          api.get('/rbac/sales-dashboard/performance'),
          api.get('/rbac/sales-dashboard/payments'),
        ]);
        setActive(a.data);
        setPerformance(p.data);
        setPayments(m.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="sales-dashboard-page">
      <div>
        <h1 className="text-4xl font-black tracking-tight">My Sales Dashboard</h1>
        <p className="text-muted-foreground mt-1">Everything below is scoped to your own clients and POs.</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-5 bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200" data-testid="card-active-equipment">
          <div className="flex items-start gap-3">
            <Truck className="h-6 w-6 text-blue-700 mt-1" />
            <div>
              <p className="text-xs uppercase tracking-wide text-blue-800 font-semibold">Active Equipment</p>
              <p className="text-3xl font-black text-blue-900 mt-1">{active.count}</p>
              <p className="text-xs text-blue-700 mt-1">across {active.dn_count} live delivery{active.dn_count !== 1 ? 'ies' : ''}</p>
            </div>
          </div>
        </Card>
        <Card className="p-5 bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200" data-testid="card-total-received">
          <div className="flex items-start gap-3">
            <CircleCheckBig className="h-6 w-6 text-emerald-700 mt-1" />
            <div>
              <p className="text-xs uppercase tracking-wide text-emerald-800 font-semibold">Payments Received</p>
              <p className="text-3xl font-black text-emerald-900 mt-1" data-testid="value-payments-received">{money(payments.total_received)}</p>
              <p className="text-xs text-emerald-700 mt-1">{payments.invoice_count} invoice{payments.invoice_count !== 1 ? 's' : ''}</p>
            </div>
          </div>
        </Card>
        <Card className="p-5 bg-gradient-to-br from-orange-50 to-red-50 border-orange-200" data-testid="card-total-pending">
          <div className="flex items-start gap-3">
            <Clock4 className="h-6 w-6 text-orange-700 mt-1" />
            <div>
              <p className="text-xs uppercase tracking-wide text-orange-800 font-semibold">Payments Pending</p>
              <p className="text-3xl font-black text-orange-900 mt-1" data-testid="value-payments-pending">{money(payments.total_pending)}</p>
              <p className="text-xs text-orange-700 mt-1">outstanding across clients</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Sales Performance */}
      <Card data-testid="card-sales-performance">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-5 w-5 text-slate-600" />
            <h2 className="font-bold text-lg">Sales Performance</h2>
            <Badge variant="outline" className="ml-auto">Total {money(performance.total_value)} · {performance.po_count} PO{performance.po_count !== 1 ? 's' : ''}</Badge>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {performance.breakdown.filter((b) => b.count > 0 || b.category !== 'other').slice(0, 4).map((b) => (
              <div
                key={b.category}
                className={`p-3 rounded-lg border ${CATEGORY_COLORS[b.label] || CATEGORY_COLORS.Other}`}
                data-testid={`perf-${b.category}`}
              >
                <p className="text-xs uppercase tracking-wide font-semibold">{b.label}</p>
                <p className="text-xl font-black mt-1">{money(b.amount)}</p>
                <p className="text-xs opacity-75 mt-0.5">{b.count} PO{b.count !== 1 ? 's' : ''}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Active Equipment table */}
      <Card data-testid="card-active-equipment-list">
        <CardContent className="p-0">
          <div className="p-4 border-b flex items-center gap-2">
            <Truck className="h-5 w-5 text-slate-600" />
            <h2 className="font-bold text-lg">Active Equipment On-Site</h2>
          </div>
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Loading...</div>
          ) : active.items.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">No equipment on-site right now.</div>
          ) : (
            <div className="divide-y">
              {active.items.slice(0, 20).map((it, idx) => (
                <div key={`${it.dn_id}-${idx}`} className="p-3 flex items-center gap-3 hover:bg-slate-50" data-testid={`active-eq-${idx}`}>
                  <MapPin className="h-4 w-4 text-slate-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-semibold">{it.registration_number || 'no plate'}</span>
                      <span className="text-sm text-muted-foreground">· {it.make} {it.model}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {it.client_name} · {it.delivery_location || 'no location'} · Delivered {it.delivery_date || '—'}
                    </p>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-800">on-site</Badge>
                </div>
              ))}
              {active.items.length > 20 && (
                <div className="p-3 text-xs text-center text-muted-foreground">+{active.items.length - 20} more</div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Tracker - by Client + by PO */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="card-by-client">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign className="h-5 w-5 text-slate-600" />
              <h2 className="font-bold text-lg">By Client</h2>
            </div>
            {payments.by_client.length === 0 ? (
              <p className="text-muted-foreground text-sm">No invoices yet.</p>
            ) : (
              <div className="space-y-2">
                {payments.by_client.slice(0, 10).map((c) => (
                  <div key={c.client_id} className="flex items-center justify-between text-sm border-b py-2 last:border-0">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{c.client_name || 'Unknown client'}</p>
                      <p className="text-xs text-muted-foreground">{c.invoice_count} invoice{c.invoice_count !== 1 ? 's' : ''}</p>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <p className="text-emerald-700 text-xs">{money(c.received)}</p>
                      <p className="text-orange-700 text-xs">{money(c.pending)} pending</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        <Card data-testid="card-by-po">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign className="h-5 w-5 text-slate-600" />
              <h2 className="font-bold text-lg">By Client PO</h2>
            </div>
            {payments.by_po.length === 0 ? (
              <p className="text-muted-foreground text-sm">No invoices yet.</p>
            ) : (
              <div className="space-y-2">
                {payments.by_po.slice(0, 10).map((p) => (
                  <div key={p.client_po_id} className="flex items-center justify-between text-sm border-b py-2 last:border-0">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{p.po_number || 'Unlinked'}</p>
                      <p className="text-xs text-muted-foreground truncate">{p.client_name} · {p.invoice_count} inv</p>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <p className="text-emerald-700 text-xs">{money(p.received)}</p>
                      <p className="text-orange-700 text-xs">{money(p.pending)} pending</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SalesDashboardPage;
